import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { supabase, ShopConfig } from "../lib/supabase";
import { useShop } from "../contexts/ShopContext";
import { useSubscription } from "../hooks/useSubscription";
import { usePlans } from "../hooks/usePlans";
import { planName } from "../lib/plans";
import { Link, useNavigate } from "react-router-dom";
import TeamAccessList from "./TeamAccessList";
import InviteModal from "./InviteModal";
import StoreStatusPanel from "./StoreStatusPanel";
import {
  CogIcon,
  ShoppingBagIcon,
  ChartBarIcon,
  BoltIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  BuildingOfficeIcon,
  BuildingStorefrontIcon,
  TagIcon
} from "@heroicons/react/24/outline";


const Dashboard: React.FC = () => {
  const { user, signOut } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [shopConfig, setShopConfig] = useState<ShopConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const { currentShopId } = useShop();
  const { planId, isActive, status, currentPeriodEnd } = useSubscription(currentShopId);
  const { plans } = usePlans();

  // Updates history state
  const [updates, setUpdates] = useState<any[]>([]);
  const [updatesLoading, setUpdatesLoading] = useState(false);
  const [updatesError, setUpdatesError] = useState<string | null>(null);

  const [inviteOpen, setInviteOpen] = useState(false);

  // Bulk products sync state
  const [bulkSyncLoading, setBulkSyncLoading] = useState(false);
  const [lastBulkStartAt, setLastBulkStartAt] = useState<number | null>(null);

  // Sync status state
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [enablingSync, setEnablingSync] = useState(false);

  useEffect(() => {
    fetchShopConfig();
  }, [user, currentShopId]);

  // Load last bulk start timestamp per shop from localStorage
  useEffect(() => {
    try {
      if (!currentShopId) { setLastBulkStartAt(null); return; }
      const raw = localStorage.getItem(`flowstock.bulkStartLast.${currentShopId}`);
      setLastBulkStartAt(raw ? parseInt(raw, 10) || null : null);
    } catch {}
  }, [currentShopId]);

  const fetchShopConfig = async () => {
    if (!user) return;

    try {
      const isDemoMode =
        process.env.REACT_APP_DEMO_MODE === "true" ||
        !process.env.REACT_APP_SUPABASE_URL ||
        process.env.REACT_APP_SUPABASE_URL.includes("placeholder");

      if (isDemoMode) {
        // Set demo configuration
        setShopConfig({
          client_name: "Demo Shop",
          platform: {
            shopify: {
              shop_domain: "demo-shop.myshopify.com",
              access_token: "demo-token",
              location_id: "demo-location-123",
            },
          },
          business_rules: {
            update_schema: {
              product: { title: true, brand: true },
              variant: { price: true, stock: false },
              clientConfig: {
                standardTags: ["FLOWSTOCK", "DEMO"],
                standardDescription: false,
                useCategoryTags: true,
              },
            },
          },
        });
        setLoading(false);
        return;
      }

      // Ensure a shop is selected
      const selectedShopId = currentShopId;
      if (!selectedShopId) {
        setShopConfig(null);
        return;
      }

      // Fetch merchant_config by merchant_id
      const { data: configRow, error: configErr} = await supabase
        .from("merchant_config")
        .select("id, config_object")
        .eq("merchant_id", selectedShopId)
        .single();
      if (configErr) throw configErr;

      // Fetch merchant is_active status
      const { data: merchant, error: merchantErr } = await supabase
        .from('merchants')
        .select('is_active')
        .eq('id', selectedShopId)
        .single();

      // Set sync enabled based on merchant is_active (default true for backward compatibility)
      setSyncEnabled(merchant?.is_active ?? true);

      // Subscription details handled by useSubscription hook

      if (configRow?.config_object) {
        const conf = configRow.config_object as ShopConfig;
        setShopConfig(conf);
      } else {
        setShopConfig(null);
      }
    } catch (error) {
      console.error("Error fetching shop config:", error);
      // Fallback to demo configuration on any error
      setShopConfig({
        client_name: "Demo Shop (Fallback)",
        platform: {
          shopify: {
            shop_domain: "demo-shop.myshopify.com",
            access_token: "demo-token",
            location_id: "demo-location-123",
          },
        },
        business_rules: {
          update_schema: {
            product: { title: true, brand: true },
            variant: { price: true, stock: false },
            clientConfig: {
              standardTags: ["FLOWSTOCK", "DEMO", "FALLBACK"],
              standardDescription: false,
              useCategoryTags: true,
            },
          },
        },
      });
    } finally {
      setLoading(false);
    }
  };


  // Load updates from update_queue filtered by connected providers for this merchant
  const loadUpdates = async () => {
    if (!user || !currentShopId) return;

    try {
      setUpdatesError(null);
      setUpdatesLoading(true);

      // Fetch connected provider ids for the selected shop (merchant)
      const { data: mappings, error: mappingsErr } = await supabase
        .from("merchant_provider_map")
        .select("provider_id")
        .eq("merchant_id", currentShopId);
      if (mappingsErr) throw mappingsErr;
      const providerIds = (mappings || [])
        .map((m: any) => m.provider_id)
        .filter(Boolean) as string[];

      if (!providerIds.length) {
        setUpdates([]);
        return;
      }

      // Fetch recent updates for those providers
      const { data: queueRows, error: qErr } = await supabase
        .from("update_queue")
        .select(
          "id, provider_id, product_description_id, product_variant_id, update_type, priority, update_data, created_at, completed_at"
        )
        .in("provider_id", providerIds)
        .order("created_at", { ascending: false })
        .limit(200);
      if (qErr) throw qErr;

      const rows = queueRows || [];
      const pdIds = Array.from(
        new Set(
          rows
            .map((r: any) => r.product_description_id)
            .filter((v: any) => !!v)
        )
      ) as string[];

      // Fetch product metadata for names and SKUs
      let productById = new Map<string, { id: string; title?: string; sku?: string }>();
      if (pdIds.length) {
        const { data: products, error: pErr } = await supabase
          .from("product_descriptions")
          .select("id, title, sku")
          .in("id", pdIds);
        if (pErr) throw pErr;
        (products || []).forEach((p: any) => {
          productById.set(p.id, { id: p.id, title: p.title, sku: p.sku });
        });
      }

      // Fetch provider metadata for display
      let providerById = new Map<string, { name?: string; type?: string }>();
      if (providerIds.length) {
        const { data: providers, error: provErr } = await supabase
          .from("providers")
          .select("id, name, provider_type")
          .in("id", providerIds);
        if (provErr) throw provErr;
        (providers || []).forEach((p: any) => {
          providerById.set(p.id, { name: p.name, type: p.provider_type });
        });
      }

      // Enrich updates for UI rendering
      const enriched = rows.map((u: any) => {
        const pd = u.product_description_id ? productById.get(u.product_description_id) : undefined;
        const ud = u.update_data || {};
        const name = pd?.title || ud.product_name || ud.name || ud.title || ud.product?.name || "Unnamed product";
        const sku = pd?.sku || ud.product_sku || ud.target_sku || ud.sku || ud.product?.sku || "—";
        const when = u.completed_at || u.created_at;
        const prov = u.provider_id ? providerById.get(u.provider_id) : undefined;
        const provider_name = prov?.name || "—";
        const provider_type = prov?.type || "";
        return { ...u, product_name: name, sku, when, provider_name, provider_type };
      });

      setUpdates(enriched);
    } catch (err: any) {
      setUpdatesError(err.message || "Failed to load updates history");
    } finally {
      setUpdatesLoading(false);
    }
  };

  const formatDateTime = (iso?: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  };

  const formatSubscriptionStatus = () => {
    if (!status) return null;

    if (status === 'trialing' && currentPeriodEnd) {
      const endDate = new Date(currentPeriodEnd);
      const formattedDate = endDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
      return `trial ends ${formattedDate}`;
    }

    return status;
  };



  // Load updates when user changes
  useEffect(() => {
    if (user) {
      loadUpdates();
    }
  }, [user]);

  const FIVE_MIN_MS = 5 * 60 * 1000;
  const msRemaining = () => {
    if (!lastBulkStartAt) return 0;
    const elapsed = Date.now() - lastBulkStartAt;
    return Math.max(0, FIVE_MIN_MS - elapsed);
  };
  const formatRemaining = (ms: number) => {
    const s = Math.ceil(ms / 1000);
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${r.toString().padStart(2, '0')}`;
  };

  const handleTriggerBulkSync = async () => {
    try {
      if (!currentShopId) throw new Error("No shop selected");
      const rem = msRemaining();
      if (rem > 0) {
        toast.error(`Please wait ${formatRemaining(rem)} before triggering again.`);
        return;
      }
      setBulkSyncLoading(true);
      const { data, error } = await supabase.functions.invoke(
        'shopify-bulk-products-start',
        { body: { merchant_id: currentShopId } }
      );
      if (error) throw new Error(error.message || 'Failed to start bulk');
      toast.success('Bulk product sync started. It may take a few minutes to complete.');
      // Persist last trigger timestamp
      const now = Date.now();
      try { localStorage.setItem(`flowstock.bulkStartLast.${currentShopId}`, String(now)); } catch {}
      setLastBulkStartAt(now);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to trigger bulk sync');
    } finally {
      setBulkSyncLoading(false);
    }
  };

  const handleEnableSync = async () => {
    setEnablingSync(true);

    try {
      if (!currentShopId) throw new Error("No shop selected");

      // Call RPC function to securely update is_active
      const { error: rpcError } = await supabase.rpc('toggle_merchant_sync', {
        p_merchant_id: currentShopId,
        p_is_active: true
      });

      if (rpcError) throw rpcError;

      // Refetch config to ensure UI reflects database state
      await fetchShopConfig();

      toast.success('Sync enabled successfully!');
    } catch (err: any) {
      console.error('Error enabling sync:', err);
      toast.error(err.message || 'Failed to enable sync');
    } finally {
      setEnablingSync(false);
    }
  };

  const handleDisableSync = async () => {
    try {
      if (!currentShopId) throw new Error("No shop selected");

      // Call RPC function to securely update is_active
      const { error: rpcError } = await supabase.rpc('toggle_merchant_sync', {
        p_merchant_id: currentShopId,
        p_is_active: false
      });

      if (rpcError) throw rpcError;

      // Refetch config to ensure UI reflects database state
      await fetchShopConfig();

      toast.success('Sync disabled successfully');
    } catch (err: any) {
      console.error('Error disabling sync:', err);
      toast.error(err.message || 'Failed to disable sync');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/40 text-sm">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 space-y-6">

        {/* Welcome bar */}
        <motion.div
          className="glass-card rounded-2xl p-6"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="font-heading font-bold text-white text-2xl mb-1">
                Welcome back,{" "}
                <span className="text-orange-400">
                  {user?.user_metadata?.name || user?.email?.split("@")[0]}
                </span>
              </h2>
              <p className="text-white/50 text-sm">
                Manage your {shopConfig?.client_name || "shop"} processors and configuration
              </p>
            </div>
            {shopConfig?.platform?.shopify && (
              <div className="flex flex-wrap items-center gap-3 text-xs">
                <span className="flex items-center gap-2 text-white/40">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
                  </span>
                  {shopConfig.platform.shopify.shop_domain}
                </span>
                {planId && (
                  <span className="bg-orange-500/10 text-orange-400 border border-orange-500/20 rounded-full px-2.5 py-1 font-semibold uppercase tracking-wide">
                    {planName(plans, planId)}
                  </span>
                )}
              </div>
            )}
          </div>
        </motion.div>

        {currentShopId && <StoreStatusPanel shopId={currentShopId} />}

        {/* Sync Status Card */}
        <motion.div
          className={`glass-card rounded-2xl p-6 ${syncEnabled ? 'border-l-2 border-l-orange-500' : ''}`}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.05 }}
        >
          <div className="flex items-start gap-3 mb-4">
            {syncEnabled ? (
              <>
                <CheckCircleIcon className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                <h3 className="font-heading font-bold text-white text-lg">Sync is Active</h3>
              </>
            ) : (
              <>
                <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <h3 className="font-heading font-bold text-white text-lg">Sync is Disabled</h3>
              </>
            )}
          </div>

          {!syncEnabled && (
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 mb-4">
              <p className="text-white/50 text-sm mb-2">Product sync is currently disabled. Before enabling:</p>
              <ol className="list-decimal list-inside space-y-1 text-sm text-white/40">
                <li>Configure your processor settings</li>
                <li>Connect at least one product provider</li>
                <li>Enable sync to start importing products</li>
              </ol>
            </div>
          )}

          {syncEnabled && (
            <p className="text-white/50 text-sm mb-4">
              Products are automatically syncing from your connected providers.
            </p>
          )}

          <div className="flex gap-3 flex-wrap">
            {!syncEnabled ? (
              <>
                <Link
                  to="/processors"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 text-white/70 hover:text-white hover:border-white/40 text-sm transition-colors"
                >
                  <CogIcon className="w-4 h-4" />
                  Configure Processors
                </Link>
                <button
                  onClick={handleEnableSync}
                  disabled={enablingSync}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500 hover:bg-orange-400 text-black font-semibold text-sm disabled:opacity-50 transition-colors"
                >
                  {enablingSync ? (
                    <><ArrowPathIcon className="w-4 h-4 animate-spin" /> Enabling...</>
                  ) : (
                    <><CheckCircleIcon className="w-4 h-4" /> Enable Sync</>
                  )}
                </button>
              </>
            ) : (
              <button
                onClick={handleDisableSync}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 text-sm transition-colors"
              >
                Disable Sync
              </button>
            )}
          </div>
        </motion.div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {[
            {
              to: '/processors',
              icon: <CogIcon className="w-5 h-5 text-orange-400" />,
              title: 'Processor Config',
              desc: 'Configure title, tags, pricing, and category processors',
              active: true,
            },
            {
              to: '/providers',
              icon: <ShoppingBagIcon className="w-5 h-5 text-orange-400" />,
              title: 'Providers',
              desc: 'View available providers, metrics, and manage connections',
              active: true,
            },
            {
              to: '/analytics',
              icon: <ChartBarIcon className="w-5 h-5 text-orange-400" />,
              title: 'Analytics',
              desc: 'Your sales, what is hot across the network, and what you are missing',
              active: true,
            },
            {
              to: '/product-lines',
              icon: <TagIcon className="w-5 h-5 text-orange-400" />,
              title: 'Product Lines',
              desc: 'Pick the brands and models you sell, across every supplier',
              active: true,
            },
            {
              to: '/settings',
              icon: <BuildingOfficeIcon className="w-5 h-5 text-orange-400" />,
              title: 'Business Config',
              desc: 'Manage shipping and billing address details',
              active: true,
            },
            {
              to: '/test-shop',
              icon: <BuildingStorefrontIcon className="w-5 h-5 text-orange-400" />,
              title: 'Test Shop',
              desc: 'View merged inventory, cheapest-provider routing, and size grid',
              active: true,
            },
          ].map((card, i) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 + i * 0.05 }}
              whileHover={card.active ? { y: -4, transition: { type: 'spring', stiffness: 400 } } : {}}
            >
              {card.to ? (
                <Link to={card.to} className="block glass-card rounded-2xl p-6 h-full">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center">
                      {card.icon}
                    </div>
                    <BoltIcon className="w-4 h-4 text-orange-500/40" />
                  </div>
                  <h3 className="font-heading font-bold text-white text-base mb-1">{card.title}</h3>
                  <p className="text-white/40 text-sm">{card.desc}</p>
                </Link>
              ) : (
                <div className="glass-card rounded-2xl p-6 h-full opacity-50 cursor-not-allowed">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 rounded-xl bg-white/[0.04] border border-white/[0.06] flex items-center justify-center">
                      {card.icon}
                    </div>
                  </div>
                  <h3 className="font-heading font-bold text-white text-base mb-1">{card.title}</h3>
                  <p className="text-white/40 text-sm">{card.desc}</p>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Configuration Preview */}
        {shopConfig && (
          <motion.div
            className="glass-card rounded-2xl p-6"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.25 }}
          >
            <h3 className="font-heading font-bold text-white text-lg mb-5">Current Configuration</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Platform */}
              <div>
                <h4 className="text-orange-400 text-xs font-semibold uppercase tracking-widest mb-3">Platform</h4>
                {shopConfig.platform?.shopify && (
                  <div className="bg-white/[0.03] rounded-xl border border-white/[0.06] p-4 font-mono text-xs text-white/50 space-y-2">
                    <div className="flex justify-between">
                      <span>Domain</span>
                      <span className="text-white/70">{shopConfig.platform.shopify.shop_domain}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Location ID</span>
                      <span className="text-white/70">{shopConfig.platform.shopify.location_id || "Not set"}</span>
                    </div>
                    <div className="pt-3 border-t border-white/[0.06]">
                      <div className="text-white/40 mb-2">Bulk product sync</div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <button
                          onClick={handleTriggerBulkSync}
                          disabled={bulkSyncLoading || msRemaining() > 0}
                          className="px-3 py-1.5 rounded-full bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 border border-orange-500/20 text-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          title={msRemaining() > 0 ? `Cooldown: ${formatRemaining(msRemaining())}` : 'Start a full products sync'}
                        >
                          {bulkSyncLoading ? 'Syncing...' : 'Start bulk sync'}
                        </button>
                        {msRemaining() > 0 && (
                          <span className="text-white/30 text-xs">Cooldown: {formatRemaining(msRemaining())}</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Update Schema */}
              <div>
                <h4 className="text-orange-400 text-xs font-semibold uppercase tracking-widest mb-3">Update Schema</h4>
                {shopConfig.business_rules?.update_schema && (
                  <div className="bg-white/[0.03] rounded-xl border border-white/[0.06] p-4 font-mono text-xs text-white/50 space-y-2">
                    <div className="flex justify-between">
                      <span>Update Titles</span>
                      <span className={shopConfig.business_rules.update_schema.product?.title ? 'text-orange-400' : 'text-white/25'}>
                        {shopConfig.business_rules.update_schema.product?.title ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Update Prices</span>
                      <span className={shopConfig.business_rules.update_schema.variant?.price ? 'text-orange-400' : 'text-white/25'}>
                        {shopConfig.business_rules.update_schema.variant?.price ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Standard Tags</span>
                      <span className="text-white/70 text-right max-w-[60%] truncate">
                        {shopConfig.business_rules.update_schema.clientConfig?.standardTags?.join(', ') || 'None'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>SKU Format</span>
                      <span className="text-white/70">
                        {shopConfig.business_rules.variant_sku_format === 'base_only' ? 'Base Only' : 'Base + Size'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Team access */}
            <div className="mt-6 pt-6 border-t border-white/[0.06] grid grid-cols-1 md:grid-cols-2 gap-6">
              <TeamAccessList shopId={currentShopId} />
              <div>
                <h4 className="font-heading font-bold text-white text-sm mb-2">Invite Teammates</h4>
                <p className="text-white/40 text-sm mb-3">Create a shareable link. Default expiry: 3 days.</p>
                <button
                  onClick={() => setInviteOpen(true)}
                  disabled={!currentShopId}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500 hover:bg-orange-400 text-black font-semibold text-sm disabled:opacity-40 transition-colors"
                >
                  Generate Invite Link
                </button>
              </div>
            </div>
            <InviteModal isOpen={inviteOpen} onClose={() => setInviteOpen(false)} shopId={currentShopId} />
          </motion.div>
        )}

        {/* Updates History */}
        <motion.div
          className="glass-card rounded-2xl p-6"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-heading font-bold text-white text-lg">Updates History</h3>
            <button
              onClick={loadUpdates}
              disabled={updatesLoading}
              className="px-3 py-1.5 rounded-full border border-white/[0.08] text-white/40 hover:text-white/70 hover:border-white/20 text-xs transition-colors disabled:opacity-40"
            >
              {updatesLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          {updatesError && <p className="text-orange-400 text-xs mb-3">{updatesError}</p>}

          <div className="overflow-x-auto">
            <div className="min-w-[640px]">
              <div className="grid grid-cols-10 gap-3 py-2 text-xs text-white/25 border-b border-white/[0.06]">
                <div className="col-span-4">Product</div>
                <div className="col-span-2">SKU</div>
                <div className="col-span-2">Provider</div>
                <div className="col-span-2">When</div>
              </div>
              <div className="h-80 overflow-y-auto">
                {(!updates || updates.length === 0) && !updatesLoading ? (
                  <div className="py-8 text-center text-white/25 text-sm">No updates found</div>
                ) : (
                  updates.map((u, idx) => {
                    const name = u.product_name || u.name || u.title || "Unnamed product";
                    const sku = u.sku || u.variant_sku || u.product_sku || "—";
                    const provider = u.provider_name || "—";
                    const when = u.when || u.completed_at || u.created_at || "";
                    const key = u.id || `${u.provider_id || "prov"}-${u.sku || idx}-${when || idx}-${idx}`;
                    return (
                      <div key={key} className="grid grid-cols-10 gap-3 items-center py-2.5 border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                        <div className="col-span-4 min-w-0">
                          <span className="text-white/70 text-sm truncate block">{name}</span>
                        </div>
                        <div className="col-span-2">
                          <span className="inline-block bg-white/[0.05] border border-white/[0.07] px-2 py-0.5 rounded text-xs font-mono text-white/40 truncate max-w-full">
                            {sku}
                          </span>
                        </div>
                        <div className="col-span-2 min-w-0">
                          <span className="text-white/40 text-xs truncate block max-w-[9rem]">{provider}</span>
                        </div>
                        <div className="col-span-2 text-white/25 font-mono text-xs whitespace-nowrap">
                          {formatDateTime(when)}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </motion.div>

      </main>
    </div>
  );
};

export default Dashboard;
